/**
 * The HR module's route table (CLAUDE.md §8.12).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads them in.
 *
 * **Every route names one permission, and it is the permission the server
 * guards the screen's primary read with** — never the write. A route gated on
 * `hr.employee.create` would hide the employee list from everyone who may only
 * read it; a route gated on nothing at all would render a screen whose every
 * request comes back 403. Write permissions are checked inside the screens, per
 * action, because §6 makes raise / approve / reveal a segregation-of-duties set
 * and no action may be inferred from another.
 *
 * `/hr/employees/new` is the one exception and deliberately so: unlike a
 * document screen it has no read-only mode at all — it exists to create
 * somebody — so it names `hr.employee.create`. It is declared **before**
 * `/hr/employees/:id(\d+)` so `new` can never be parsed as an employee id; the
 * `\d+` constraint makes that safe anyway, the ordering makes it obvious.
 */

import type { RouteRecordRaw } from 'vue-router'

import { HR_PERMISSIONS } from '@/modules/hr/permissions'
import { HR_C11_PERMISSIONS } from '@/modules/hr/permissionsC11'

export const HR_ROUTES: RouteRecordRaw[] = [
  /* ----------------------------------------------------------- employees */
  {
    path: '/hr/employees',
    name: 'hr-employees',
    component: () => import('@/modules/hr/EmployeesListView.vue'),
    meta: { titleKey: 'hr.routeTitles.employees', permission: HR_PERMISSIONS.employeeRead },
  },
  {
    path: '/hr/employees/new',
    name: 'hr-employee-new',
    component: () => import('@/modules/hr/EmployeeView.vue'),
    meta: { titleKey: 'hr.routeTitles.employeeNew', permission: HR_PERMISSIONS.employeeCreate },
  },
  {
    path: '/hr/employees/:id(\\d+)',
    name: 'hr-employee',
    component: () => import('@/modules/hr/EmployeeView.vue'),
    meta: { titleKey: 'hr.routeTitles.employee', permission: HR_PERMISSIONS.employeeRead },
  },
  {
    path: '/hr/work-permits',
    name: 'hr-work-permits',
    /* C10 replaced the derived screen with the real register: `GET /work-permits`
       with its own filters, quota consumption and a per-permit detail. The route is
       unchanged so the nav link and every existing bookmark keep working. */
    component: () => import('@/modules/hr/WorkPermitsListView.vue'),
    meta: { titleKey: 'hr.routeTitles.workPermits', permission: HR_PERMISSIONS.employeeRead },
  },

  /* --------------------------------------------------------------- leave */
  {
    path: '/hr/leave/requests',
    name: 'hr-leave-requests',
    component: () => import('@/modules/hr/LeaveRequestsListView.vue'),
    meta: { titleKey: 'hr.routeTitles.leaveRequests', permission: HR_PERMISSIONS.leaveRead },
  },
  {
    path: '/hr/leave/calendar',
    name: 'hr-leave-calendar',
    component: () => import('@/modules/hr/LeaveCalendarView.vue'),
    meta: { titleKey: 'hr.routeTitles.leaveCalendar', permission: HR_PERMISSIONS.leaveRead },
  },
  {
    path: '/hr/leave/types',
    name: 'hr-leave-types',
    component: () => import('@/modules/hr/LeaveTypesView.vue'),
    meta: { titleKey: 'hr.routeTitles.leaveTypes', permission: HR_PERMISSIONS.leaveTypeRead },
  },

  /* ---------------------------------------------------------- attendance */
  {
    path: '/hr/attendance',
    name: 'hr-attendance',
    component: () => import('@/modules/hr/AttendanceListView.vue'),
    meta: { titleKey: 'hr.routeTitles.attendance', permission: HR_PERMISSIONS.attendanceRead },
  },
  {
    path: '/hr/overtime-rules',
    name: 'hr-overtime-rules',
    component: () => import('@/modules/hr/OvertimeRulesView.vue'),
    meta: {
      titleKey: 'hr.routeTitles.overtimeRules',
      permission: HR_PERMISSIONS.overtimeRuleRead,
    },
  },

  /* --------------------------------------------------------- recruitment */
  {
    path: '/hr/requisitions',
    name: 'hr-requisitions',
    component: () => import('@/modules/hr/RequisitionsListView.vue'),
    meta: { titleKey: 'hr.routeTitles.requisitions', permission: HR_PERMISSIONS.requisitionRead },
  },
  {
    path: '/hr/requisitions/:id(\\d+)',
    name: 'hr-requisition',
    component: () => import('@/modules/hr/RequisitionView.vue'),
    meta: { titleKey: 'hr.routeTitles.requisition', permission: HR_PERMISSIONS.requisitionRead },
  },
  {
    path: '/hr/candidates',
    name: 'hr-candidates',
    component: () => import('@/modules/hr/CandidatesListView.vue'),
    meta: { titleKey: 'hr.routeTitles.candidates', permission: HR_PERMISSIONS.candidateRead },
  },
  {
    path: '/hr/candidates/:id(\\d+)',
    name: 'hr-candidate',
    component: () => import('@/modules/hr/CandidateView.vue'),
    meta: { titleKey: 'hr.routeTitles.candidate', permission: HR_PERMISSIONS.candidateRead },
  },
  {
    path: '/hr/offers',
    name: 'hr-offers',
    component: () => import('@/modules/hr/OffersListView.vue'),
    meta: { titleKey: 'hr.routeTitles.offers', permission: HR_PERMISSIONS.offerRead },
  },

  /* ------------------------------------------ skills and certifications */
  {
    /* `hr.skill.read`, not `hr.skill.manage`: a planner checking who holds a
       statutory certificate is not the person who edits the catalogue, and the
       expiring list is a compliance read the whole planning side needs. */
    path: '/hr/skills',
    name: 'hr-skills',
    component: () => import('@/modules/hr/SkillsView.vue'),
    meta: { titleKey: 'hr.routeTitles.skills', permission: HR_C11_PERMISSIONS.skillRead },
  },
]
